package nl.volleyplanner.api.types

import kotlinx.serialization.Serializable
import kotlin.math.pow
import kotlin.math.round

fun Double.toBigNumber(precision: Int) = BigNumber(this, precision)

@Serializable
class BigNumber(
    private val doubleValue: Double,
    private val precision: Int
) : Number(), Comparable<BigNumber> {

    constructor(intValue: Int, precision: Int) : this(intValue.toDouble(), precision)

    init {
        require(precision in 0..MAX_PRECISION) { "Precision must be in range of 0 to $MAX_PRECISION (provided: $precision)" }
    }

    companion object {
        val ZERO = BigNumber(0.0, 3)
        internal const val MAX_PRECISION = 17
    }

    private fun roundToPrecision(): Double {
        val factor = 10.0.pow(precision)
        return round(doubleValue * factor) / factor
    }

    operator fun plus(b: BigNumber) = BigNumber(roundToPrecision() + b.roundToPrecision(), precision)
    operator fun plus(b: Int) = BigNumber(roundToPrecision() + b, precision)
    operator fun plus(b: Double) = BigNumber(roundToPrecision() + BigNumber(b, precision).roundToPrecision(), precision)

    operator fun minus(b: BigNumber) = BigNumber(roundToPrecision() - b.roundToPrecision(), precision)
    operator fun minus(b: Int) = BigNumber(roundToPrecision() - b, precision)
    operator fun minus(b: Double) = BigNumber(roundToPrecision() - BigNumber(b, precision).roundToPrecision(), precision)

    operator fun times(b: BigNumber) = BigNumber(roundToPrecision() * b.roundToPrecision(), precision)
    operator fun times(b: Int) = BigNumber(roundToPrecision() * b, precision)
    operator fun times(b: Double) = BigNumber(roundToPrecision() * BigNumber(b, precision).roundToPrecision(), precision)

    operator fun div(b: BigNumber) = BigNumber(roundToPrecision() / b.roundToPrecision(), precision)
    operator fun div(b: Int) = BigNumber(roundToPrecision() / b, precision)
    operator fun div(b: Double) = BigNumber(roundToPrecision() / BigNumber(b, precision).roundToPrecision(), precision)

    operator fun rem(b: BigNumber) = BigNumber(roundToPrecision() % b.roundToPrecision(), precision)
    operator fun rem(b: Int) = BigNumber(roundToPrecision() % b, precision)
    operator fun rem(b: Double) = BigNumber(roundToPrecision() % BigNumber(b, precision).roundToPrecision(), precision)

    operator fun compareTo(other: Int) = roundToPrecision().compareTo(other)
    operator fun compareTo(other: Double) = roundToPrecision().compareTo(BigNumber(other, precision).roundToPrecision())
    override fun compareTo(other: BigNumber) = roundToPrecision().compareTo(other.roundToPrecision())

    override fun toString() = roundToPrecision().toString()

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other == null || this::class != other::class) return false

        other as BigNumber

        return roundToPrecision() == other.roundToPrecision()
    }

    override fun hashCode() = roundToPrecision().hashCode()

    // Following deprecation of toByte() in Double recommending to convert to Int first
    override fun toByte() = roundToPrecision().toInt().toByte()

    override fun toDouble() = roundToPrecision()

    override fun toFloat() = roundToPrecision().toFloat()

    override fun toInt() = roundToPrecision().toInt()

    override fun toLong() = roundToPrecision().toLong()

    // Following deprecation of toByte() in Double recommending to convert to Int first
    override fun toShort() = roundToPrecision().toInt().toShort()
}